import React, { useState, useRef, useEffect } from 'react';
import { useAtom } from 'jotai/index';
import classNames from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import QRCodeStyling from 'qr-code-styling';
import styles from '../css/index.module.scss';
import LogoText from '../../../assets/images/logo_text.png';
import TelegramQRIcon from '../../../assets/icons/telegramQR.svg';
import {
  widgetDataAtom,
  widgetInputsAtom,
  widgetParamsAtom,
  widgetQueryParamsAtom,
  widgetScreenStateAtom,
  widgetSelectedLanguageAtom,
} from '../../../atoms/widgetAtoms';
import useWidget from '../../../hooks/UseWidget';
import WidgetsServices from '../../../services/WidgetsServices';
import WT from '../constants/widgetTranslations';
import widgetStates from '../constants/widgetStates';
import EmptyHeight from '../../../components/EmptyHeight';
import MyButton from '../../../components/MyButton';

function WidgetFooter({ screen }) {
  const { t, makeBook, repeatWidget } = useWidget();
  const [widgetParams] = useAtom(widgetParamsAtom);
  const [widgetData] = useAtom(widgetDataAtom);
  const [widgetInputs] = useAtom(widgetInputsAtom);
  const [, setWidgetScreenState] = useAtom(widgetScreenStateAtom);
  const [widgetSelectedLanguage] = useAtom(widgetSelectedLanguageAtom);
  const [widgetQueryParams] = useAtom(widgetQueryParamsAtom);

  const [isCaptchaVisible, setIsCaptchaVisible] = useState(false);
  const qrCodeRef = useRef(null);

  const isInputsDisabled = !widgetInputs.name.length || widgetInputs.phoneNumber.replaceAll('_', '').length !== 16;

  const showTelegramBlock = widgetData?.send_through_telegram_bot && widgetData?.telegram_bot_name;
  const telegramUrl = showTelegramBlock ? `https://t.me/${widgetData.telegram_bot_name}` : '';

  const getWidgetMainColor = () => {
    const fromCss = typeof document !== 'undefined'
      && getComputedStyle(document.documentElement).getPropertyValue('--widget-main-color').trim();
    if (fromCss) return fromCss;
    if (WidgetsServices.checkIfColorIsValid(widgetQueryParams?.colorMain)) return widgetQueryParams.colorMain;
    return '#645375';
  };
 
  useEffect(() => {
    if (!showTelegramBlock || !qrCodeRef.current) return;
    const container = qrCodeRef.current;
    let raf1;
    let raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        container.innerHTML = '';
        const mainColor = getWidgetMainColor();
        const qrCode = new QRCodeStyling({
          width: 100,
          height: 100,
          type: 'svg',
          data: telegramUrl,
          dotsOptions: {
            color: mainColor,
            type: 'dots',
          },
          cornersSquareOptions: { type: 'extra-rounded' },
          cornersDotOptions: { type: 'rounded' },
          backgroundOptions: { color: '#fff' },
        });
        qrCode.append(container);
      });
    });

    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [showTelegramBlock, telegramUrl, widgetQueryParams?.colorMain]);

  const onBookClick = () => {
    setIsCaptchaVisible(true);
  };

  const onCaptchaChange = async (val) => {
    if (val) {
      makeBook({ captchaToken: val });
    }
  };

  const policyBlock = (
    <>
      <EmptyHeight height={16} />
      <p className={styles.policyText}>
        {t(WT.policy1, screen === widgetStates.slots ? t(WT.continue) : t(WT.book))}
        <a target="_blank" href="https://reservista.ru/documents/reservista_users_agreement.pdf" rel="noreferrer">{t(WT.policy2)}</a>
      </p>
    </>
  );

  const renderContent = () => {
    if (screen === widgetStates.slots) {
      return (
        <>
          <MyButton
            text={t(WT.continue)}
            className={classNames(styles.myButton, { [styles.myButtonDisabled]: !widgetInputs.slot })}
            disabled={!widgetInputs.slot}
            onClick={() => widgetData?.max_guests >= widgetParams.persons && setWidgetScreenState(widgetStates.inputs)}
          />
          {policyBlock}
        </>
      );
    }

    if (screen === widgetStates.inputs) {
      if (isCaptchaVisible) {
        return (
          <>
            {policyBlock}
          </>
        );
      }
      return (
        <>
          <MyButton
            text={t(WT.book)}
            className={classNames(styles.myButton, { [styles.myButtonDisabled]: isInputsDisabled })}
            disabled={isInputsDisabled}
            onClick={() => !isInputsDisabled && onBookClick()}
          />
          {policyBlock}
        </>
      );
    }

    if (screen === widgetStates.success) {
      return (
        <MyButton
          text={t(WT.repeat)}
          className={styles.myButton}
          onClick={repeatWidget}
        />
      );
    }

    return null;
  };

  return (
    <div className={styles.footer}>
      {screen === widgetStates.inputs && isCaptchaVisible && (
        <>
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
            onChange={onCaptchaChange}
            hl={widgetSelectedLanguage?.code || 'en'}
          />
          <EmptyHeight height={16} />
        </>
      )}
      {showTelegramBlock && (
        <div className={styles.telegramPart}>
          <div className={styles.telegramTop}>
            <a target="_blank" href={telegramUrl} rel="noreferrer" className={styles.qrCodeLink}>
              <div className={styles.qrCodeWrapper}>
                <div ref={qrCodeRef} className={styles.qrCode} />
                <div className={styles.qrCodeCenter}>
                  <img src={TelegramQRIcon} alt="" />
                </div>
              </div>
            </a>
            <p className={styles.telegramText}>{widgetData.widget_telegram_bot_description}</p>
          </div>
          <a target="_blank" href={telegramUrl} rel="noreferrer" className={styles.telegramUsername}>
            @{widgetData.telegram_bot_name}
          </a>
        </div>
      )}
      {renderContent()}
      {screen !== widgetStates.inputs && (
        <div className={styles.footerLogo}>
          <img src={LogoText} alt="Reservista" />
        </div>
      )}
    </div>
  );
}

export default WidgetFooter;
